/**
 * The notification rules (phase 7b item 4): per alert type on or off and its
 * severity, and the cooldown. Controlled — the draft is the container's
 * (`AlertSettingsView`), so the live-update banner (D35) can compare it with
 * what the server now holds; this only renders it and reports changes.
 * Site overrides are kept as they are (the CLI edits them: `acp alerts
 * settings set --site-rule`); their count is shown.
 */
import { ALERT_CATALOG, ALERT_TYPES, SEVERITIES, type AlertType, type Severity } from '@acp/tenant-alerts/catalog.js';
import type { AlertRuleWire } from '@acp/api-contract/alerts.js';
import type { RulesDraft } from '../../lib/alerts.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Field, FieldDescription } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type AlertRulesFormProps = {
  readonly draft: RulesDraft;
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly error: string | null;
  readonly onChange: (draft: RulesDraft) => void;
  readonly onSave: () => void;
};

function withRule(draft: RulesDraft, type: AlertType, change: { enabled?: boolean; severity?: Severity }): RulesDraft {
  const rule = draft.rules[type];
  if (rule === undefined) return draft;
  return { ...draft, rules: { ...draft.rules, [type]: { enabled: change.enabled ?? rule.enabled, severity: change.severity ?? rule.severity } } };
}

/**
 * The rows: every type this build knows that the server answered a rule for. A type the server does not know (it is
 * older than this page) is not offered — saving a rule for it would be refused.
 */
function ruleRows(draft: RulesDraft): (readonly [AlertType, AlertRuleWire])[] {
  return ALERT_TYPES.flatMap((type) => {
    const rule = draft.rules[type];
    return rule === undefined ? [] : [[type, rule] as const];
  });
}

export function AlertRulesForm({ draft, dirty, saving, error, onChange, onSave }: AlertRulesFormProps) {
  const cooldownValid = Number.isInteger(draft.cooldownMinutes) && draft.cooldownMinutes >= 1 && draft.cooldownMinutes <= 1440;
  return (
    <Card data-testid="alert-rules">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Rules</CardTitle>
          <CardDescription>Which alerts are sent, and how severe each is.</CardDescription>
        </div>
        <Button onClick={onSave} disabled={!dirty || saving || !cooldownValid} data-testid="save-rules">
          {saving ? 'Saving…' : 'Save rules'}
        </Button>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error !== null ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <Field className="max-w-xs" invalid={!cooldownValid}>
          <Label htmlFor="alert-cooldown">Cooldown (minutes)</Label>
          <Input
            id="alert-cooldown"
            type="number"
            min={1}
            max={1440}
            value={Number.isFinite(draft.cooldownMinutes) ? String(draft.cooldownMinutes) : ''}
            onChange={(event) => onChange({ ...draft, cooldownMinutes: Number(event.target.value) })}
            data-testid="cooldown"
          />
          <FieldDescription>The shortest time between two notifications about one alert. 1 to 1440.</FieldDescription>
        </Field>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">On</TableHead>
              <TableHead>Alert</TableHead>
              <TableHead className="w-40">Severity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ruleRows(draft).map(([type, rule]) => (
              <TableRow key={type} data-testid="rule-row" data-type={type}>
                <TableCell>
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    aria-label={`Send ${ALERT_CATALOG[type].label}`}
                    checked={rule.enabled}
                    onChange={(event) => onChange(withRule(draft, type, { enabled: event.target.checked }))}
                  />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{ALERT_CATALOG[type].label}</div>
                  <div className="text-sm text-muted-foreground">{ALERT_CATALOG[type].description}</div>
                </TableCell>
                <TableCell>
                  <NativeSelect
                    aria-label={`Severity of ${ALERT_CATALOG[type].label}`}
                    value={rule.severity}
                    onChange={(event) => {
                      const severity = SEVERITIES.find((s) => s === event.target.value);
                      if (severity !== undefined) onChange(withRule(draft, type, { severity }));
                    }}
                  >
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </NativeSelect>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {draft.siteOverrides.length > 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="site-overrides">
            {draft.siteOverrides.length} site override(s) apply ({draft.siteOverrides.map((o) => o.siteId).join(', ')}); edit them with <code>acp alerts settings set --site-rule</code>.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
